/**
 * `JobAdmin` — pagina admin per la gestione dei Job (professioni e-CF 3.0).
 *
 * Modellata su `CompetenceAdmin`. Un Job aggrega un insieme di **competenze**
 * richieste, ciascuna con un peso di importanza (`1..5`) e un Livello richiesto:
 * la relazione Job↔Competenza è l'analogo, un livello più in alto, della
 * relazione Competenza↔Skill.
 *
 * CRUD su `/api/jobs`:
 *  - `GET /api/jobs`        → elenco;
 *  - `POST /api/jobs`       → creazione;
 *  - `PUT /api/jobs/:id`    → aggiornamento;
 *  - `DELETE /api/jobs/:id` → cancellazione;
 *  - `POST /api/jobs/import`→ import massivo da CSV (de‑dup per nome).
 *
 * L'associazione alle competenze (con peso e Livello richiesto) riusa il
 * componente generico `SkillWeightEditor`, alimentato con l'elenco delle
 * competenze (`GET /api/competences`). L'autorizzazione autorevole è dell'API
 * (`requireAdmin` per ogni mutazione): questa pagina è comunque riservata
 * all'admin dal routing.
 */
import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type CompetenceDto,
  type CreateJobRequest,
  type ImportJobRow,
  type ImportJobsResponse,
  type JobDto,
  type Livello,
  type UpdateJobRequest,
} from '../shared/dto';
import { useApiClient } from '../api/useApiClient';
import {
  parseJobsCsv,
  parseJobCompetencesCsv,
  CsvParseError,
} from '../utils/parseJobsCsv';
import type {
  ImportJobCompetencesEntry,
  ImportJobCompetencesResponse,
} from '../shared/dto';
import {
  Alert,
  Button,
  DataTable,
  Field,
  Icon,
  Loading,
  Modal,
  PageHeader,
  TextInput,
  TextArea,
  SkillWeightEditor,
  type Column,
  type SortDirection,
} from '../components/ui';

const jobsKey = ['jobs'] as const;
const competencesKey = ['competences'] as const;

/** Confronto per nome già orientato per la direzione (per `DataTable`). */
function compareByName(a: JobDto, b: JobDto, direction: SortDirection): number {
  const sign = direction === 'desc' ? -1 : 1;
  const va = a.name.toLowerCase();
  const vb = b.name.toLowerCase();
  if (va < vb) return -sign;
  if (va > vb) return sign;
  return 0;
}

/** Stato di modifica di un Job. */
interface EditingState {
  id: string;
  name: string;
  summary: string;
  mission: string;
  deliverables: string;
  tasks: string;
  kpi: string;
  competenceIds: string[];
  competenceWeights: Record<string, number>;
  competenceRequiredLevels: Record<string, Livello>;
}

export default function JobAdmin() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newSummary, setNewSummary] = useState('');
  const [newMission, setNewMission] = useState('');
  const [newDeliverables, setNewDeliverables] = useState('');
  const [newTasks, setNewTasks] = useState('');
  const [newKpi, setNewKpi] = useState('');
  const [newCompetenceIds, setNewCompetenceIds] = useState<string[]>([]);
  const [newCompetenceWeights, setNewCompetenceWeights] = useState<
    Record<string, number>
  >({});
  const [newCompetenceRequiredLevels, setNewCompetenceRequiredLevels] = useState<
    Record<string, Livello>
  >({});
  const [editing, setEditing] = useState<EditingState | null>(null);
  const [query, setQuery] = useState('');

  const jobsQuery = useQuery({
    queryKey: jobsKey,
    queryFn: () => api.get<JobDto[]>('/api/jobs'),
  });
  const competencesQuery = useQuery({
    queryKey: competencesKey,
    queryFn: () => api.get<CompetenceDto[]>('/api/competences'),
  });

  const invalidateJobs = () => {
    void queryClient.invalidateQueries({ queryKey: jobsKey });
  };

  const resetCreateForm = () => {
    setNewName('');
    setNewSummary('');
    setNewMission('');
    setNewDeliverables('');
    setNewTasks('');
    setNewKpi('');
    setNewCompetenceIds([]);
    setNewCompetenceWeights({});
    setNewCompetenceRequiredLevels({});
  };

  const createJob = useMutation({
    mutationFn: (body: CreateJobRequest) => api.post<JobDto>('/api/jobs', body),
    onSuccess: () => {
      resetCreateForm();
      setIsCreateOpen(false);
      invalidateJobs();
    },
  });

  const updateJob = useMutation({
    mutationFn: (vars: { id: string; body: UpdateJobRequest }) =>
      api.put<JobDto>(`/api/jobs/${vars.id}`, vars.body),
    onSuccess: () => {
      setEditing(null);
      invalidateJobs();
    },
  });

  const deleteJob = useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/jobs/${id}`),
    onSuccess: () => invalidateJobs(),
  });

  // --- Import da CSV -------------------------------------------------------
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [csvError, setCsvError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<ImportJobsResponse | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const importJobs = useMutation({
    mutationFn: (rows: ImportJobRow[]) =>
      api.post<ImportJobsResponse>('/api/jobs/import', { jobs: rows }),
    onSuccess: (result) => {
      setImportResult(result);
      invalidateJobs();
    },
  });

  // Import multi-file delle relazioni Job↔Competenza: stesso popup, secondo
  // input file (il nome del file è il nome della professione).
  const bulkInputRef = useRef<HTMLInputElement>(null);
  const [relImportResult, setRelImportResult] =
    useState<ImportJobCompetencesResponse | null>(null);

  const importJobCompetences = useMutation({
    mutationFn: (entries: ImportJobCompetencesEntry[]) =>
      api.post<ImportJobCompetencesResponse>('/api/jobs/import-competences', {
        entries,
      }),
    onSuccess: (result) => {
      setRelImportResult(result);
      invalidateJobs();
    },
  });

  const openImport = () => {
    setImportResult(null);
    setRelImportResult(null);
    setCsvError(null);
    importJobs.reset();
    importJobCompetences.reset();
    setIsImportOpen(true);
  };

  const closeImport = () => {
    setIsImportOpen(false);
    setCsvError(null);
    setImportResult(null);
    setRelImportResult(null);
    importJobs.reset();
    importJobCompetences.reset();
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (bulkInputRef.current) bulkInputRef.current.value = '';
  };

  const handleCsvFile = async (file: File) => {
    setCsvError(null);
    setImportResult(null);
    setRelImportResult(null);
    importJobs.reset();
    importJobCompetences.reset();
    try {
      const text = await file.text();
      const rows = parseJobsCsv(text);
      importJobs.mutate(rows);
    } catch (err) {
      setCsvError(
        err instanceof CsvParseError ? err.message : 'Impossibile leggere il file CSV.',
      );
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  /** Nome del job dal nome file: rimuove il path e l'estensione `.csv`. */
  const jobNameFromFile = (fileName: string): string =>
    fileName.replace(/\.csv$/i, '').replace(/^.*[\\/]/, '').trim();

  /**
   * Import multi-file delle relazioni: per ogni CSV selezionato deduce il nome
   * della professione dal nome del file e ne legge le coppie competenza→livello.
   * Le competenze si risolvono per nome dal catalogo esistente (lato server).
   */
  const handleBulkFiles = async (files: File[]) => {
    setCsvError(null);
    setImportResult(null);
    setRelImportResult(null);
    importJobs.reset();
    importJobCompetences.reset();
    try {
      const entries: ImportJobCompetencesEntry[] = [];
      for (const file of files) {
        const jobName = jobNameFromFile(file.name);
        if (jobName === '') continue;
        const text = await file.text();
        const competences = parseJobCompetencesCsv(text);
        entries.push({ jobName, competences });
      }
      if (entries.length === 0) {
        setCsvError('Nessun file valido selezionato.');
        return;
      }
      importJobCompetences.mutate(entries);
    } catch (err) {
      setCsvError(
        err instanceof CsvParseError ? err.message : 'Impossibile leggere i file CSV.',
      );
    } finally {
      if (bulkInputRef.current) bulkInputRef.current.value = '';
    }
  };

  const jobs = useMemo(() => jobsQuery.data ?? [], [jobsQuery.data]);
  const competences = useMemo(
    () => competencesQuery.data ?? [],
    [competencesQuery.data],
  );

  // Descrizione per competence id: alimenta l'hint "i" accanto al nome
  // nell'editor delle associazioni.
  const competenceDescriptions = useMemo(() => {
    const map: Record<string, string | undefined> = {};
    for (const c of competences) map[c.id] = c.description;
    return map;
  }, [competences]);

  const normalizedQuery = query.trim().toLowerCase();
  const visible = useMemo(() => {
    if (normalizedQuery === '') return jobs;
    return jobs.filter(
      (j) =>
        j.name.toLowerCase().includes(normalizedQuery) ||
        (j.summary ?? '').toLowerCase().includes(normalizedQuery),
    );
  }, [jobs, normalizedQuery]);

  const trimmedNew = newName.trim();
  const trimmedEdit = editing?.name.trim() ?? '';

  const columns = useMemo<Column<JobDto>[]>(
    () => [
      {
        key: 'name',
        header: 'Professione',
        sortable: true,
        sortComparator: compareByName,
        render: (j) => (
          <div className="admin-table__identity">
            <span className="admin-table__name">{j.name}</span>
          </div>
        ),
      },
      {
        key: 'summary',
        header: 'Sintesi',
        render: (j) => (
          <span className="admin-table__assoc">{j.summary ?? '—'}</span>
        ),
      },
      {
        key: 'competences',
        header: 'Competenze richieste',
        render: (j) => (
          <span className="admin-table__assoc">{j.competenceIds.length}</span>
        ),
      },
      {
        key: 'actions',
        header: 'Azioni',
        className: 'admin-table__actions-col',
        render: (j) => (
          <div className="admin-item__actions">
            <Button
              type="button"
              size="sm"
              iconOnly
              aria-label={`Modifica ${j.name}`}
              title={`Modifica ${j.name}`}
              onClick={() =>
                setEditing({
                  id: j.id,
                  name: j.name,
                  summary: j.summary ?? '',
                  mission: j.mission ?? '',
                  deliverables: j.deliverables ?? '',
                  tasks: j.tasks ?? '',
                  kpi: j.kpi ?? '',
                  competenceIds: [...j.competenceIds],
                  competenceWeights: { ...j.competenceWeights },
                  competenceRequiredLevels: { ...j.competenceRequiredLevels },
                })
              }
            >
              <Icon name="edit" />
            </Button>
            <Button
              type="button"
              size="sm"
              variant="danger"
              iconOnly
              aria-label={`Elimina ${j.name}`}
              title={`Elimina ${j.name}`}
              onClick={() => deleteJob.mutate(j.id)}
              loading={deleteJob.isPending}
            >
              <Icon name="trash" />
            </Button>
          </div>
        ),
      },
    ],
    [deleteJob],
  );

  return (
    <section className="job-admin" aria-labelledby="job-admin-title">
      <PageHeader
        title="Amministrazione — Professioni"
        titleId="job-admin-title"
        subtitle="Gestisci le professioni (e-CF 3.0) e le competenze richieste, con peso e livello."
        actions={
          <div className="admin-toolbar">
            <Button type="button" variant="default" onClick={openImport}>
              Importa da CSV
            </Button>
            <Button type="button" variant="primary" onClick={() => setIsCreateOpen(true)}>
              Nuova professione
            </Button>
          </div>
        }
      />

      {/* Ricerca a piena larghezza, stesso formato delle altre sezioni admin. */}
      <Field
        id="job-search"
        label="Cerca professione"
        hint="Filtra le professioni per nome o sintesi."
      >
        <div className="competence-search">
          <TextInput
            id="job-search"
            className="competence-search__input"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cerca per nome o sintesi…"
            autoComplete="off"
            aria-label="Cerca professione per nome o sintesi"
          />
        </div>
      </Field>

      {/* Import Job da file CSV (de‑dup per nome lato server). */}
      <Modal
        open={isImportOpen}
        onClose={closeImport}
        title="Importa professioni da CSV"
        footer={
          <>
            <Button type="button" variant="ghost" onClick={closeImport}>
              {importResult || relImportResult ? 'Chiudi' : 'Annulla'}
            </Button>
            <Button
              type="button"
              variant="default"
              loading={importJobCompetences.isPending}
              disabled={importJobs.isPending || importJobCompetences.isPending}
              onClick={() => bulkInputRef.current?.click()}
            >
              Importa relazioni (più CSV)…
            </Button>
            <Button
              type="button"
              variant="primary"
              loading={importJobs.isPending}
              disabled={importJobs.isPending || importJobCompetences.isPending}
              onClick={() => fileInputRef.current?.click()}
            >
              Scegli file CSV…
            </Button>
          </>
        }
      >
        <div className="admin-form">
          <p>
            Carica un file CSV con una colonna <code>name</code> (obbligatoria) e,
            facoltativamente, <code>summary</code>, <code>mission</code>,{' '}
            <code>deliverables</code>, <code>tasks</code> e <code>kpi</code>. Il nome è
            la chiave: le professioni nuove vengono create, quelle già presenti vengono
            aggiornate (i campi vuoti nel CSV non sovrascrivono i valori esistenti).
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="sr-only"
            aria-label="File CSV da importare"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleCsvFile(file);
            }}
          />

          <p className="admin-form__bulk-hint">
            In alternativa, con <strong>Importa relazioni (più CSV)</strong> puoi
            selezionare più file insieme (es. la cartella <code>job-competences/</code>):
            per ciascuno il <em>nome del file</em> (senza <code>.csv</code>) è il nome
            della professione e le righe hanno intestazione <code>name</code> (competenza)
            e <code>level</code> (livello richiesto, <code>e-0</code>…<code>e-5</code>).
            Le competenze si risolvono per nome dal catalogo esistente; le professioni
            assenti vengono create (associazioni non distruttive).
          </p>

          <input
            ref={bulkInputRef}
            type="file"
            accept=".csv,text/csv"
            multiple
            className="sr-only"
            aria-label="Più file CSV delle relazioni da importare (professione dedotta dal nome file)"
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              if (files.length > 0) void handleBulkFiles(files);
            }}
          />

          {csvError && <Alert tone="error">{csvError}</Alert>}

          {importJobs.isError && (
            <Alert tone="error">Import non riuscito. Verifica il file e riprova.</Alert>
          )}

          {importResult && (
            <Alert
              tone={
                importResult.created > 0 || importResult.updated > 0 ? 'success' : 'info'
              }
            >
              Create {importResult.created} professioni, {importResult.updated} aggiornate
              {importResult.updatedNames.length > 0 && (
                <> ({importResult.updatedNames.join(', ')})</>
              )}
              {importResult.skipped > 0 && (
                <>
                  , {importResult.skipped} saltate
                  {importResult.skippedNames.length > 0 && (
                    <> (duplicati nel CSV: {importResult.skippedNames.join(', ')})</>
                  )}
                </>
              )}
              .
            </Alert>
          )}

          {importJobCompetences.isError && (
            <Alert tone="error">
              Import delle relazioni non riuscito. Verifica i file e riprova.
            </Alert>
          )}

          {relImportResult && (
            <div className="admin-form__bulk-results">
              <Alert
                tone={
                  relImportResult.created > 0 || relImportResult.linkedCompetences > 0
                    ? 'success'
                    : 'info'
                }
              >
                Relazioni importate: {relImportResult.created} professioni create,{' '}
                {relImportResult.updated} aggiornate, {relImportResult.linkedCompetences}{' '}
                associazioni create.
                {relImportResult.missingCompetences.length > 0 && (
                  <>
                    {' '}
                    Competenze non trovate a catalogo:{' '}
                    {relImportResult.missingCompetences.join(', ')}.
                  </>
                )}
              </Alert>
              <ul className="admin-form__bulk-list">
                {relImportResult.jobs.map((j) => (
                  <li key={j.jobName}>
                    <strong>{j.jobName}</strong> → {j.linkedCompetences} competenze
                    {j.created ? ' (professione creata)' : ''}
                    {j.missingCompetences.length > 0 && (
                      <> · mancanti: {j.missingCompetences.join(', ')}</>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Modal>

      {/* Creazione con associazione N:N alle competenze. */}
      <Modal
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        title="Nuova professione"
        size="wide"
        footer={
          <>
            <Button type="button" variant="ghost" onClick={() => setIsCreateOpen(false)}>
              Annulla
            </Button>
            <Button
              type="submit"
              form="job-create-form"
              variant="primary"
              loading={createJob.isPending}
              disabled={trimmedNew.length === 0}
            >
              Crea
            </Button>
          </>
        }
      >
        <form
          id="job-create-form"
          className="admin-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (trimmedNew.length === 0) return;
            const body: CreateJobRequest = {
              name: trimmedNew,
              ...(newSummary.trim() ? { summary: newSummary.trim() } : {}),
              ...(newMission.trim() ? { mission: newMission.trim() } : {}),
              ...(newDeliverables.trim() ? { deliverables: newDeliverables.trim() } : {}),
              ...(newTasks.trim() ? { tasks: newTasks.trim() } : {}),
              ...(newKpi.trim() ? { kpi: newKpi.trim() } : {}),
              competenceIds: newCompetenceIds,
              competenceWeights: newCompetenceWeights,
              competenceRequiredLevels: newCompetenceRequiredLevels,
            };
            createJob.mutate(body);
          }}
        >
          <Field id="job-new-name" label="Nome">
            <TextInput
              id="job-new-name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Es. Data Scientist"
            />
          </Field>
          <Field id="job-new-summary" label="Sintesi">
            <TextInput
              id="job-new-summary"
              value={newSummary}
              onChange={(e) => setNewSummary(e.target.value)}
              placeholder="Sintesi facoltativa"
            />
          </Field>
          <Field id="job-new-mission" label="Mission">
            <TextArea
              id="job-new-mission"
              rows={2}
              value={newMission}
              onChange={(e) => setNewMission(e.target.value)}
              placeholder="Mission facoltativa"
            />
          </Field>
          <Field id="job-new-deliverables" label="Deliverable">
            <TextArea
              id="job-new-deliverables"
              rows={2}
              value={newDeliverables}
              onChange={(e) => setNewDeliverables(e.target.value)}
              placeholder="Deliverable principali (facoltativo)"
            />
          </Field>
          <Field id="job-new-tasks" label="Compiti">
            <TextArea
              id="job-new-tasks"
              rows={2}
              value={newTasks}
              onChange={(e) => setNewTasks(e.target.value)}
              placeholder="Compiti/attività principali (facoltativo)"
            />
          </Field>
          <Field id="job-new-kpi" label="KPI">
            <TextInput
              id="job-new-kpi"
              value={newKpi}
              onChange={(e) => setNewKpi(e.target.value)}
              placeholder="Indicatore chiave di performance (facoltativo)"
            />
          </Field>
          <Field
            id="job-new-competences"
            label="Competenze richieste, peso (1 = marginale, 5 = essenziale) e livello richiesto"
          >
            {competencesQuery.isLoading ? (
              <Loading>Caricamento competenze…</Loading>
            ) : (
              <SkillWeightEditor
                id="job-new-competences"
                options={competences}
                descriptions={competenceDescriptions}
                selectedIds={newCompetenceIds}
                weights={newCompetenceWeights}
                requiredLevels={newCompetenceRequiredLevels}
                onChangeIds={setNewCompetenceIds}
                onChangeWeights={setNewCompetenceWeights}
                onChangeRequiredLevels={setNewCompetenceRequiredLevels}
                placeholder="Cerca una competenza…"
                emptyMessage="Nessuna competenza corrisponde."
              />
            )}
          </Field>
        </form>
      </Modal>

      {/* Modifica di una professione. */}
      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title="Modifica professione"
        size="wide"
        footer={
          <>
            <Button type="button" variant="ghost" onClick={() => setEditing(null)}>
              Annulla
            </Button>
            <Button
              type="submit"
              form="job-edit-form"
              variant="primary"
              loading={updateJob.isPending}
              disabled={trimmedEdit.length === 0}
            >
              Salva
            </Button>
          </>
        }
      >
        <form
          id="job-edit-form"
          className="admin-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!editing || trimmedEdit.length === 0) return;
            const body: UpdateJobRequest = {
              name: trimmedEdit,
              summary: editing.summary.trim(),
              mission: editing.mission.trim(),
              deliverables: editing.deliverables.trim(),
              tasks: editing.tasks.trim(),
              kpi: editing.kpi.trim(),
              competenceIds: editing.competenceIds,
              competenceWeights: editing.competenceWeights,
              competenceRequiredLevels: editing.competenceRequiredLevels,
            };
            updateJob.mutate({ id: editing.id, body });
          }}
        >
          <Field id="job-edit-name" label="Nome">
            <TextInput
              id="job-edit-name"
              value={editing?.name ?? ''}
              onChange={(e) =>
                setEditing((prev) => (prev ? { ...prev, name: e.target.value } : prev))
              }
            />
          </Field>
          <Field id="job-edit-summary" label="Sintesi">
            <TextInput
              id="job-edit-summary"
              value={editing?.summary ?? ''}
              onChange={(e) =>
                setEditing((prev) => (prev ? { ...prev, summary: e.target.value } : prev))
              }
              placeholder="Sintesi facoltativa"
            />
          </Field>
          <Field id="job-edit-mission" label="Mission">
            <TextArea
              id="job-edit-mission"
              rows={2}
              value={editing?.mission ?? ''}
              onChange={(e) =>
                setEditing((prev) => (prev ? { ...prev, mission: e.target.value } : prev))
              }
              placeholder="Mission facoltativa"
            />
          </Field>
          <Field id="job-edit-deliverables" label="Deliverable">
            <TextArea
              id="job-edit-deliverables"
              rows={2}
              value={editing?.deliverables ?? ''}
              onChange={(e) =>
                setEditing((prev) =>
                  prev ? { ...prev, deliverables: e.target.value } : prev,
                )
              }
              placeholder="Deliverable principali (facoltativo)"
            />
          </Field>
          <Field id="job-edit-tasks" label="Compiti">
            <TextArea
              id="job-edit-tasks"
              rows={2}
              value={editing?.tasks ?? ''}
              onChange={(e) =>
                setEditing((prev) => (prev ? { ...prev, tasks: e.target.value } : prev))
              }
              placeholder="Compiti/attività principali (facoltativo)"
            />
          </Field>
          <Field id="job-edit-kpi" label="KPI">
            <TextInput
              id="job-edit-kpi"
              value={editing?.kpi ?? ''}
              onChange={(e) =>
                setEditing((prev) => (prev ? { ...prev, kpi: e.target.value } : prev))
              }
              placeholder="Indicatore chiave di performance (facoltativo)"
            />
          </Field>
          <Field
            id="job-edit-competences"
            label="Competenze richieste, peso (1 = marginale, 5 = essenziale) e livello richiesto"
          >
            <SkillWeightEditor
              id="job-edit-competences"
              options={competences}
              descriptions={competenceDescriptions}
              selectedIds={editing?.competenceIds ?? []}
              weights={editing?.competenceWeights ?? {}}
              requiredLevels={editing?.competenceRequiredLevels ?? {}}
              onChangeIds={(next) =>
                setEditing((prev) => (prev ? { ...prev, competenceIds: next } : prev))
              }
              onChangeWeights={(next) =>
                setEditing((prev) =>
                  prev ? { ...prev, competenceWeights: next } : prev,
                )
              }
              onChangeRequiredLevels={(next) =>
                setEditing((prev) =>
                  prev ? { ...prev, competenceRequiredLevels: next } : prev,
                )
              }
              placeholder="Cerca una competenza…"
              emptyMessage="Nessuna competenza corrisponde."
            />
          </Field>
        </form>
      </Modal>

      {(createJob.isError || updateJob.isError || deleteJob.isError) && (
        <Alert tone="error">Impossibile salvare la modifica. Riprova.</Alert>
      )}

      {jobsQuery.isLoading ? (
        <Loading>Caricamento professioni…</Loading>
      ) : (
        <DataTable
          rows={visible}
          columns={columns}
          rowKey={(j) => j.id}
          error={jobsQuery.isError ? 'Impossibile caricare le professioni.' : null}
          emptyMessage={
            normalizedQuery !== ''
              ? 'Nessuna professione corrisponde alla ricerca.'
              : 'Nessuna professione configurata. Crea la prima con “Nuova professione”.'
          }
          defaultSort={{ key: 'name', direction: 'asc' }}
          pageSize={10}
        />
      )}
    </section>
  );
}
